/**
 * Backend sözleşmesi: docs/api.md. Yollar **göreli** — dev'de Vite `/api` proxy'ler,
 * prod'da aynı origin'den `api` süreci sunar (stack.md). Böylece CORS yok, env yok.
 */

import type { Region } from '../camera/useCamera'

export type CharacterStatus = 'QUEUED' | 'GENERATING' | 'NORMALIZING' | 'READY' | 'FAILED'

/** Sunucunun sahibi olduğu açılış değerleri — kiosk env kopyalamaz. */
interface KioskConfig {
  eventId: string
}

export interface CreatedCharacter {
  characterId: string
  playerId: string
  displayName: string
  status: CharacterStatus
  statusUrl: string
  trackUrl: string
  qrPngBase64: string
}

interface CharacterState {
  characterId: string
  displayName: string
  status: CharacterStatus
  outfitId: string
  failureReason?: string
  packageUrl?: string
}

/**
 * Kafayı gövdeye oturtmak için gereken yerleşim sayıları (ADR-018).
 *
 * Sprite boyu ve boyun noktasının oranı **karakter başına** değişiyor: tuval içerikten türüyor,
 * yani hacimli saçlı bir kafanın tuvali daha büyük. Bu sayılar bilinmezse kafa boyundan kayar
 * (pivot) ve tuval genişliğine göre ölçeklenirse yüz büyüyüp küçülür (ölçek).
 *
 * `undefined` → eski kayıt; yerleşim referans değerlere düşer, sabit varsayılmaz.
 */
export interface ShowcaseHead {
  pivotY: number
  width: number
  height: number
  pixelsPerUnit: number
}

export interface ShowcaseCharacter {
  characterId: string
  displayName: string
  outfitId: string
  status: CharacterStatus
  previewUrl: string | null
  packageUrl: string | null
  updatedAt: string
  head?: ShowcaseHead
  /**
   * Victory Gallery aggregate'leri (feature/victory-gallery, 2026-08-29) -- backend'de henüz
   * yok, `/api/events/{eventId}/characters` bunları döndürmüyor bugün. Hepsi optional: alan
   * yoksa UI rozet basmaz, tahmin/0 varsaymaz (ADR-009 ruhu -- eksik veri "yok" değil
   * "bilinmiyor"dur). Deniz'e backend enhancement olarak iletildi; response'a eklenince bu
   * arayüz zaten hazır, ek kod değişikliği gerekmez.
   */
  matchCount?: number
  winCount?: number
  lossCount?: number
  victoryCardCount?: number
}

export interface StatusChangedEvent {
  type: string
  eventId: string
  characterId: string
  displayName: string
  status: CharacterStatus
  updatedAt: string
  failureReason?: string
  packageUrl?: string
}

/**
 * Silinen kart (#94). `status` **yok**: silinen karakter READY kalır, tek doğru tepki onu
 * listeden düşürmektir — durum değişimi sanılıp gride geri konmamalı.
 */
interface CharacterDeletedEvent {
  type: string
  eventId: string
  characterId: string
  deletedAt: string
}

/** `POST /api/frames/check` yanıtı — kiosk deklanşörünü bu açar (#31). */
export interface FrameVerdict {
  faceFound: boolean
  eyesOpen: boolean
  sharp: boolean
  framed: boolean
  ok: boolean
  hint: string | null
  /** Ham ölçümler — eşik kalibrasyonu ve kişiye göre göz kıyaslaması için. */
  measurements?: {
    faceRatio: number
    centerX: number
    centerY: number
    eyeOpenness: number
    sharpness: number
    rollDeg: number
  }
}

/** Kiosk kapısı (#131): kapı kapanınca yüzey ne yapacak — kiosk bunu "yenile"ye bağlar. */
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // 401 = kapı kapandı (süre doldu ya da parola değişti). Parola denemesinin kendisi
    // bu yoldan geçmez; `unlockKiosk` yanıtı elden okur.
    if (res.status === 401) onUnauthorized?.()
    const detail = await res.text()
    let message = detail
    try {
      const body: unknown = JSON.parse(detail)
      if (body && typeof body === 'object' && 'detail' in body) {
        const d = (body as { detail: unknown }).detail
        message = typeof d === 'string' ? d : JSON.stringify(d)
      }
    } catch {
      // düz metin gövde: olduğu gibi kalsın
    }
    throw new ApiError(res.status, message || `HTTP ${res.status}`)
  }
  return (await res.json()) as T
}

export async function fetchConfig(signal?: AbortSignal): Promise<KioskConfig> {
  return json<KioskConfig>(await fetch('/api/config', signal ? { signal } : {}))
}

/** Kapının durumu (#131). `required: false` → env'de parola yok, kapı hiç kurulmamış. */
export interface KioskSession {
  required: boolean
  unlocked: boolean
  expiresInSeconds: number | null
}

export async function fetchKioskSession(): Promise<KioskSession> {
  return json<KioskSession>(await fetch('/api/kiosk/session'))
}

/**
 * Parolayı **sunucuya** sorar; doğruysa erişim çerezi sunucudan gelir (HttpOnly, JS okuyamaz).
 * `false` = parola yanlış; ağ/sunucu hatası fırlatır — ikisi misafire aynı şey değil.
 */
export async function unlockKiosk(password: string): Promise<boolean> {
  const res = await fetch('/api/kiosk/unlock', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  if (res.status === 401) return false
  if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`)
  return true
}

/** 202 döner: karakter QUEUED, üretim worker'da. Takip QR'ı yanıtta gelir. */
export async function createCharacter(input: {
  photo: Blob
  displayName: string
  outfitId: string
  /** Kılavuz ovalinin **ham karedeki** yeri (#98); yoksa sunucu tüm kareyi üretime yollar. */
  region?: Region | null
}): Promise<CreatedCharacter> {
  const form = new FormData()
  form.append('photo', input.photo, 'portrait.png')
  form.append('displayName', input.displayName)
  form.append('outfitId', input.outfitId)
  if (input.region) form.append('region', JSON.stringify(input.region))
  return json<CreatedCharacter>(await fetch('/api/characters', { method: 'POST', body: form }))
}

/**
 * Çerçeveleme karesini ölçtürür. Kare **sunucuda saklanmaz**; üretimle aynı dedektörle bakılır,
 * böylece kioskun onayladığı kare worker'ın işleyebildiği karedir.
 */
export async function checkFrame(frame: Blob, signal?: AbortSignal): Promise<FrameVerdict> {
  const form = new FormData()
  form.append('frame', frame, 'frame.jpg')
  return json<FrameVerdict>(
    await fetch('/api/frames/check', { method: 'POST', body: form, ...(signal ? { signal } : {}) }),
  )
}

export async function fetchCharacter(id: string, signal?: AbortSignal): Promise<CharacterState> {
  return json<CharacterState>(
    await fetch(`/api/characters/${encodeURIComponent(id)}`, signal ? { signal } : {}),
  )
}

/**
 * Misafir kendini beğenmedi (#94): kayıt kalır, misafirin yüzünü taşıyan dosyalar sunucuda
 * silinir. Geri alınamaz — çağıran yüzey onayı **önce** almış olmalı.
 *
 * 404 = zaten yok (silinmiş ya da hiç olmamış): kart gridden düşeceği için hata sayılmaz.
 * 409 = üretim sürüyor; o kart zaten "hazırlanıyor" halindedir, düğme gösterilmez.
 */
export async function deleteCharacter(id: string): Promise<void> {
  const res = await fetch(`/api/characters/${encodeURIComponent(id)}`, { method: 'DELETE' })
  if (res.ok || res.status === 404) return
  await json<never>(res) // gövdedeki `detail`i ApiError'a çevirir
}

export async function fetchReadyCharacters(
  eventId: string,
  signal?: AbortSignal,
): Promise<ShowcaseCharacter[]> {
  const body = await json<{ characters: ShowcaseCharacter[] }>(
    await fetch(
      `/api/events/${encodeURIComponent(eventId)}/characters?status=READY&limit=200`,
      signal ? { signal } : {},
    ),
  )
  return body.characters
}

// Backend'in Outcome StrEnum'ıyla birebir (victory/domain/model/__init__.py) -- canlı veriyle
// doğrulandı (2026-08-29): "LOSS", "lose" değil.
export type VictoryOutcome = 'WIN' | 'LOSS' | 'DRAW'
export type VictoryStatus = 'QUEUED' | 'GENERATING' | 'READY' | 'FAILED'

export interface VictoryEntry {
  victoryId: string
  matchId: string
  status: VictoryStatus
  outcome: VictoryOutcome
  headline: string
  caption: string
  /** Yalnız status READY iken gelir -- zaten kalıcı/public bir URL (#41, victoryId tahmin
   * edilemez, immutable cache) -- QR için doğrudan kullanılabilir, kısa rotaya gerek yok.
   * **PNG'dir ve öyle kalır:** paylaşılan bağlantı budur, ekranda gösterilmez (#139). */
  posterUrl?: string
  /** Ekranda gösterilen tam boy poster -- aynı çizim, ~8 kat küçük (#139). */
  posterWebpUrl?: string
  /** Izgaradaki küçük resim (432x540). Posterle aynı oranda: kırpmayı CSS yapar (#139). */
  thumbUrl?: string
}

/**
 * Character Detail / Victory Gallery modalı için (feature/victory-gallery, 2026-08-29) --
 * `docs/api.md`'deki mevcut uç, yeni bir şey değil. Kartın kendisi Unity'nin
 * `POST /api/matches/ended`'ıyla arka planda otomatik üretiliyor; bu yalnız okuyor.
 */
export async function fetchVictories(
  characterId: string,
  limit = 50,
  signal?: AbortSignal,
): Promise<VictoryEntry[]> {
  const body = await json<{ characterId: string; victories: VictoryEntry[] }>(
    await fetch(
      `/api/characters/${encodeURIComponent(characterId)}/victories?limit=${limit}`,
      signal ? { signal } : {},
    ),
  )
  return body.victories
}

/**
 * SSE akışı (ADR-015). EventSource kopan bağlantıyı kendi yeniden kurar; yine de
 * çağıran taraf poll yedeğini bırakmaz — olay kaybı misafiri ekranda asılı bırakır.
 */
export function subscribeToEvents(
  eventId: string,
  onStatusChanged: (event: StatusChangedEvent) => void,
  onConnectionChange?: (connected: boolean) => void,
  onDeleted?: (event: CharacterDeletedEvent) => void,
): () => void {
  const source = new EventSource(`/api/events/${encodeURIComponent(eventId)}/stream`)
  source.addEventListener('open', () => onConnectionChange?.(true))
  source.addEventListener('error', () => onConnectionChange?.(false))
  source.addEventListener('character.status_changed', (e) => {
    try {
      onStatusChanged(JSON.parse((e as MessageEvent<string>).data) as StatusChangedEvent)
    } catch {
      // bozuk gövde: olayı at, akışı kapatma
    }
  })
  source.addEventListener('character.deleted', (e) => {
    try {
      onDeleted?.(JSON.parse((e as MessageEvent<string>).data) as CharacterDeletedEvent)
    } catch {
      // bozuk gövde: olayı at, akışı kapatma
    }
  })
  return () => source.close()
}
